import { Select, type SelectProps } from 'antd';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useIntl } from '@umijs/max';
import { getBrandList } from './service';
import type { Brand } from './service';

interface BrandSelectProps extends Omit<SelectProps, 'options'> {
  value?: number;
  onChange?: (value: number) => void;
  /** 防抖延迟时间（毫秒），默认 300ms */
  debounceDelay?: number;
}

/**
 * 品牌选择器组件
 * 支持搜索功能，自动从后端获取品牌列表
 * 搜索功能已集成防抖，减少不必要的 API 请求
 */
const BrandSelect: React.FC<BrandSelectProps> = ({
  value,
  onChange,
  debounceDelay = 300,
  placeholder,
  ...restProps
}) => {
  const intl = useIntl();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 加载品牌列表
  const loadBrands = async (brandName?: string) => {
    try {
      setLoading(true);
      const res = await getBrandList({
        page: 1,
        pageSize: 100,
        brandName,
        status: 1, // 只加载启用的品牌
      });

      if (res.code === 200 && res.data?.list) {
        setBrands(res.data.list);
      }
    } catch (error) {
      console.error('加载品牌列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载品牌列表
  useEffect(() => {
    loadBrands();
  }, []);

  // 清理防抖定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 防抖搜索处理
  const handleSearch = useCallback(
    (value: string) => {
      // 清除之前的定时器
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // 设置新的定时器
      debounceTimerRef.current = setTimeout(() => {
        if (value) {
          loadBrands(value);
        } else {
          loadBrands();
        }
      }, debounceDelay);
    },
    [debounceDelay],
  );

  // 转换为 Select 组件需要的 options 格式
  const options = brands.map((brand) => ({
    label: brand.brandName,
    value: brand.id,
  }));

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder || intl.formatMessage({ id: 'component.placeholder.select' })}
      loading={loading}
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      filterOption={false} // 使用服务端搜索，禁用前端过滤
      options={options}
      {...restProps}
    />
  );
};

export default BrandSelect;
